import { useEffect, useState } from "react";
import { Button, Descriptions, Drawer, Input, Modal, Progress, Slider, Space, Spin, Steps, Timeline, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ACT_FLOW, type ActionStatus } from "@audit-plan/shared";
import { Link } from "react-router-dom";
import { api } from "../../api/client";
import { useAction } from "../../api/actions";
import { useEvidenceList } from "../../api/evidence";
import { invalidateAudit, useAudit } from "../../api/audits";
import { usePeople } from "../../api/people";
import { useAuth } from "../../auth/AuthContext";
import { ActionStatusPill, DueLabel, PriorityPill, SeverityPill, fmtD, fmtDT } from "../pills";
import { EvidencePanel } from "../evidence/EvidencePanel";
import { FilePicker } from "../evidence/FilePicker";
import { UploadHint, UploadQueueList } from "../evidence/UploadQueueList";
import { useUploadQueue } from "../evidence/useUploadQueue";
import { useNotify } from "../audit/useNotify";
import { useFullDrawer, useFullModal } from "../audit/mobileModal";
import { useIsMobile } from "../useIsMobile";
import { ActionFormModal } from "./ActionFormModal";
import { Sensitive } from "../Sensitive";

export function ActionDrawer(props: { actionId: string | null; onClose: () => void }) {
  const { actionId, onClose } = props;
  const { t } = useTranslation();
  const { can } = useAuth();
  const people = usePeople();
  const qc = useQueryClient();
  const n = useNotify();
  const q = useAction(actionId);
  const x = q.data;
  const audit = useAudit(x?.auditId).data;
  const evq = useEvidenceList({ targetType: "action", targetId: actionId ?? undefined }, !!actionId);
  const [progress, setProgress] = useState(0);
  const [note, setNote] = useState("");
  const queue = useUploadQueue();
  const mobile = useIsMobile();
  const fd = useFullDrawer(760);
  const fm = useFullModal();
  const [rejecting, setRejecting] = useState(false);
  const [rejNote, setRejNote] = useState("");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setProgress(x?.progress ?? 0);
    setNote("");
    queue.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [x?.id, x?.progress, x?.status]);

  const refresh = () => {
    invalidateAudit(qc, x?.auditId);
    void qc.invalidateQueries({ queryKey: ["action", actionId] });
    void qc.invalidateQueries({ queryKey: ["evidence"] });
  };
  const transition = useMutation({
    mutationFn: (to: ActionStatus) => api.post(`/actions/${actionId}/transition`, { to }),
    onSuccess: refresh,
    onError: n.fail,
  });
  const reject = useMutation({
    mutationFn: (text: string) => api.post(`/actions/${actionId}/reject`, { note: text }),
    onSuccess: () => {
      setRejecting(false);
      setRejNote("");
      refresh();
    },
    onError: n.fail,
  });

  const ctxAud = audit ? { auditorId: audit.auditorId, supplierId: x?.supplierId } : { supplierId: x?.supplierId };
  const rev = !!x && (audit ? can("action.review", ctxAud) : !x.auditId && can("action.review", { supplierId: x.supplierId }));
  const supOk = !!x && (can("action.supplier", { supplierId: x.supplierId }) || (rev && !x.assigneeId)); // senza utenza fornitore l'auditor registra per conto del responsabile
  const idx = x ? ACT_FLOW.indexOf(x.status) : 0;
  const evTarget = x ? { supplierId: x.supplierId, auditId: x.auditId, target: { type: "action" as const, id: x.id } } : null;

  const submitProgress = async () => {
    if (!x || !evTarget) return;
    if (!queue.items.length) return n.warn(t("b_progress_attach"));
    setBusy(true);
    try {
      const res = await queue.upload(evTarget, note);
      const first = res.evidence[0];
      if (!res.ok || !first) {
        n.warn(t("b_up_some_failed"));
        return;
      }
      await api.post(`/actions/${x.id}/progress`, { progress, note, evidenceId: first.id });
      n.ok();
      queue.reset();
      setNote("");
      refresh();
    } catch (e) {
      n.fail(e);
    } finally {
      setBusy(false);
    }
  };

  const buttons: React.ReactNode[] = [];
  if (x) {
    if (rev && !["done", "closed"].includes(x.status))
      buttons.push(
        <Button key="edit" size={mobile ? "large" : undefined} onClick={() => setEditing(true)}>
          {t("edit")}
        </Button>,
      );
    if (supOk && x.status === "open")
      buttons.push(
        <Button key="start" size={mobile ? "large" : undefined} type="primary" loading={transition.isPending} onClick={() => transition.mutate("progress")}>
          {t("act_start")}
        </Button>,
      );
    if (supOk && x.status === "progress")
      buttons.push(
        <Button key="submit" size={mobile ? "large" : undefined} type="primary" loading={transition.isPending} onClick={() => transition.mutate("verify")}>
          {t("act_submit")}
        </Button>,
      );
    if (rev && x.status === "verify")
      buttons.push(
        <Button key="rej" size={mobile ? "large" : undefined} danger onClick={() => setRejecting(true)}>
          {t("act_reject")}
        </Button>,
        <Button key="appr" size={mobile ? "large" : undefined} type="primary" loading={transition.isPending} onClick={() => transition.mutate("done")}>
          {t("act_approve")}
        </Button>,
      );
    if (rev && x.status === "done")
      buttons.push(
        <Button key="close" size={mobile ? "large" : undefined} type="primary" loading={transition.isPending} onClick={() => transition.mutate("closed")}>
          {t("act_close")}
        </Button>,
      );
  }

  return (
    <Drawer
      open={!!actionId}
      onClose={onClose}
      {...fd}
      title={<span style={{ display: "block", whiteSpace: mobile ? "normal" : undefined, fontSize: mobile ? 15 : undefined, lineHeight: 1.3 }}>{x ? `${x.code} · ${x.title}` : ""}</span>}
      extra={mobile ? null : <Space>{buttons}</Space>}
      footer={mobile && buttons.length ? <div style={{ display: "grid", gridTemplateColumns: buttons.length > 1 ? "1fr 1fr" : "1fr", gap: 10 }}>{buttons.map((b) => b)}</div> : undefined}
      destroyOnHidden
    >
      {!x ? (
        <Spin />
      ) : (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          {mobile ? (
            <div>
              <div style={{ marginBottom: 6 }}>
                <b>{t(`as_${x.status}`)}</b> <Typography.Text type="secondary">{idx + 1}/{ACT_FLOW.length}</Typography.Text>
              </div>
              <div style={{ display: "flex", gap: 4 }}>
                {ACT_FLOW.map((s, i) => (
                  <div key={s} style={{ flex: 1, height: 6, borderRadius: 3, background: x.status === "closed" || i < idx ? "#2E6B4F" : i === idx ? "#1A4470" : "#E2DED3" }} />
                ))}
              </div>
            </div>
          ) : (
            <Steps size="small" current={x.status === "closed" ? ACT_FLOW.length : idx} items={ACT_FLOW.map((s) => ({ title: t(`as_${s}`) }))} />
          )}
          <Descriptions column={mobile ? 1 : 2} size="small" bordered>
            <Descriptions.Item label={t("f_factory")}>{x.factoryName}</Descriptions.Item>
            <Descriptions.Item label={t("supplier")}>{x.supplierName}</Descriptions.Item>
            <Descriptions.Item label="NC">
              <Link to={`/ncs?id=${x.ncId}`} onClick={onClose}>
                {x.ncCode}
              </Link>{" "}
              <SeverityPill s={x.ncSeverity} />
            </Descriptions.Item>
            <Descriptions.Item label="Audit">
              {x.auditId ? <Link to={`/audits/${x.auditId}`}>{x.auditCode}</Link> : "—"}
            </Descriptions.Item>
            <Descriptions.Item label={t("assignee")}><Sensitive>{x.assigneeId ? people.name(x.assigneeId) : x.assigneeName || "—"}</Sensitive></Descriptions.Item>
            <Descriptions.Item label={t("followedBy")}>{people.name(x.ownerId)}</Descriptions.Item>
            <Descriptions.Item label={t("priority")}>
              <PriorityPill p={x.priority} />
            </Descriptions.Item>
            <Descriptions.Item label={t("openDate")}>{fmtD(x.openDate)}</Descriptions.Item>
            <Descriptions.Item label={t("startDate")}>{fmtD(x.startDate)}</Descriptions.Item>
            <Descriptions.Item label={t("dueDate")}>
              <DueLabel a={x} />
            </Descriptions.Item>
            <Descriptions.Item label={t("status")}>
              <ActionStatusPill a={x} />
            </Descriptions.Item>
          </Descriptions>
          {x.description ? <Typography.Paragraph style={{ whiteSpace: "pre-wrap", margin: 0 }}>{x.description}</Typography.Paragraph> : null}

          <div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <b>{t("progress")}</b>
              <span>{x.progress}%</span>
            </div>
            {(supOk || rev) && !["done", "closed", "verify"].includes(x.status) ? (
              <Space direction="vertical" style={{ width: "100%" }} size={mobile ? 12 : 8}>
                <div className="ap-big-slider">
                  <Slider min={0} max={100} step={5} value={progress} onChange={setProgress} tooltip={{ formatter: (v) => `${v}%` }} />
                </div>
                {progress !== x.progress ? <Typography.Text type="secondary">{t("b_progress_set", { p: progress })}</Typography.Text> : null}
                <Input.TextArea autoSize={{ minRows: mobile ? 2 : 1, maxRows: 5 }} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("b_progress_note")} style={{ fontSize: mobile ? 16 : undefined }} />
                <FilePicker disabled={busy} onFiles={queue.add} />
                <UploadQueueList items={queue.items} busy={busy} onRemove={queue.remove} onRetry={submitProgress} />
                <Typography.Text type="secondary">{t("b_progress_attach")}</Typography.Text> <UploadHint />
                <div style={{ display: "flex", gap: 8 }}>
                  <Button type="primary" size={mobile ? "large" : "middle"} block={mobile} style={mobile ? { height: 48 } : undefined} loading={busy} disabled={!queue.items.length} onClick={submitProgress}>
                    {queue.items.some((i) => i.status === "error") && !busy ? t("b_up_retry") : t("save")}
                  </Button>
                  {busy ? <Button size={mobile ? "large" : "middle"} style={mobile ? { height: 48 } : undefined} onClick={queue.cancel}>{t("b_up_cancel")}</Button> : null}
                </div>
              </Space>
            ) : (
              <Progress percent={x.progress} status="success" showInfo={false} strokeColor="#2E6B4F" />
            )}
          </div>

          {evTarget ? (
            <EvidencePanel evidence={evq.data ?? []} ctx={evTarget} canUpload={supOk || rev} onChanged={refresh} />
          ) : null}

          <div>
            <Typography.Title level={5}>{t("b_activity_history")}</Typography.Title>
            <Timeline
              items={[...x.history].reverse().map((h) => ({
                children: (
                  <div>
                    <div>
                      <b>{people.show(h.by)}</b> · {h.to ? t(`as_${h.to}`) : ""} {h.progress != null ? `· ${h.progress}%` : ""}
                    </div>
                    {h.note ? <Typography.Text type="secondary">{h.note}</Typography.Text> : null}
                    <div>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {fmtDT(h.ts)}
                      </Typography.Text>
                    </div>
                  </div>
                ),
              }))}
            />
          </div>
        </Space>
      )}
      <Modal {...fm} open={rejecting} title={t("act_reject")} onCancel={() => setRejecting(false)} okText={t("act_reject")} cancelText={t("cancel")} okButtonProps={{ danger: true, disabled: !rejNote.trim() }} confirmLoading={reject.isPending} onOk={() => reject.mutate(rejNote.trim())}>
        <Typography.Text>{t("reviewNote")} *</Typography.Text>
        <Input.TextArea rows={3} value={rejNote} onChange={(e) => setRejNote(e.target.value)} />
      </Modal>
      {x ? <ActionFormModal open={editing} onClose={() => setEditing(false)} action={x} onSaved={refresh} /> : null}
    </Drawer>
  );
}
